import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getApi, postApi, patchApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import PropertyImageModal from './PropertyImageModal';
import { PROPERTY_LISTING_STATUS } from '../../../constants/propertyListingStatus';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Opened from a lease's View details: that flow is about rent, not
  // applicants, so the Recent Applicants list stays hidden there.
  const showApplicants = !location.state?.fromLease;
  const [property, setProperty] = useState(null);
  const [llcLogoError, setLlcLogoError] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [propertyAmenityIds, setPropertyAmenityIds] = useState([]); // Store property's amenity IDs
  const [propertyImages, setPropertyImages] = useState([]); // All property images
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // Current image in carousel
  const [isImageModalOpen, setIsImageModalOpen] = useState(false); // Modal state
  const [modalImageIndex, setModalImageIndex] = useState(0); // Current image in modal
  const [completeLoading, setCompleteLoading] = useState(false);
  const carouselIntervalRef = useRef(null); // Ref for carousel interval

  // Property type mapping
  const propertyTypeMap = {
    '1': 'Apartment',
    '2': 'House',
    '3': 'Townhouse',
    '4': 'Studio',
    '5': 'Other'
  };

  // Fetch property details (metadata is fetched after we have property_type from this response)
  useEffect(() => {
    const transformProperty = (apiData) => {
      // Get all property images
      const images = apiData.property_images && apiData.property_images.length > 0
        ? apiData.property_images.map(img => img.image_url)
        : ['https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800'];
      
      // Get first property image or use placeholder
      const firstImage = images[0];
      
      // Format address
      const address = apiData.address || '';
      const unitNumber = apiData.unit_number ? `, ${apiData.unit_number}` : '';
      const fullAddress = `${address}${unitNumber}`;
      
      // Format city and state
      const cityState = apiData.city && apiData.state 
        ? `${apiData.city}, ${apiData.state}`
        : apiData.city || apiData.state || '';
      
      // Format price
      const price = apiData.monthly_rent 
        ? `$${apiData.monthly_rent.toLocaleString()}`
        : 'N/A';
      
      // Format square feet
      const sqft = apiData.square_feet 
        ? `${apiData.square_feet.toLocaleString()} sq.ft`
        : 'N/A';
      
      // Format property type
      const type = propertyTypeMap[apiData.property_type] || 'Apartment';
      
      // Format deposit
      const deposit = apiData.security_deposit 
        ? `$${apiData.security_deposit.toLocaleString()}`
        : 'N/A';
      
      // Use property_code from API, fallback to first 5 characters of ID if not available
      const code = apiData.property_code || (apiData.id ? apiData.id.substring(0, 5) : '');
      
      // Parse amenities - can be array of objects with id/name OR array of IDs OR JSON string
      let propertyAmenities = [];
      try {
        if (apiData.amenities) {
          let parsedAmenities = typeof apiData.amenities === 'string' 
            ? JSON.parse(apiData.amenities) 
            : apiData.amenities;
          
          if (Array.isArray(parsedAmenities)) {
            // Check if it's an array of objects (with id property) or array of IDs
            if (parsedAmenities.length > 0 && typeof parsedAmenities[0] === 'object' && parsedAmenities[0].id) {
              // Array of objects: extract IDs
              propertyAmenities = parsedAmenities.map(amenity => String(amenity.id));
            } else {
              // Array of IDs: convert to strings
              propertyAmenities = parsedAmenities.map(id => String(id));
            }
          }
        }
      } catch (error) {
        console.error('Error parsing amenities:', error);
        propertyAmenities = [];
      }
      
      // Map API applicants to UI shape; status from property_tenant_status: 0=Pending, 1=Approved, 2=Declined
      const getApplicantStatusLabel = (value) => {
        const v = value != null ? String(value) : '0';
        if (v === '1') return 'Approved';
        if (v === '2') return 'Declined';
        return 'Pending';
      };
      const applicants = (apiData.applicants || []).map((app) => ({
        id: app.id || app.screening_id,
        screening_id: app.screening_id,
        firstName: app.tenant?.first_name || '',
        lastName: app.tenant?.last_name || '',
        profile_image: app.tenant?.profile_image,
        grade: app.grade != null && app.grade !== '' ? app.grade : '—',
        status: getApplicantStatusLabel(app.property_tenant_status),
      }));

      return {
        id: apiData.id,
        image: firstImage,
        address: fullAddress,
        code: code,
        city: cityState,
        zipCode: apiData.zip_code || '',
        price: price,
        sqft: sqft,
        type: type,
        deposit: deposit,
        bedrooms: apiData.bedrooms || 0,
        bathrooms: apiData.bathrooms || 0,
        description: apiData.description || '',
        applicants,
        totalApplicants: apiData.total_applicants ?? applicants.length,
        status: apiData.status != null ? String(apiData.status) : null,
        llcName: apiData.llc_name || '',
        llcLogo: apiData.llc_logo || '',
      };
    };

    const fetchProperty = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const response = await getApi(`/property/${id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          // Parse property amenities first
          let propertyAmenities = [];
          try {
            if (response.data.amenities) {
              let parsedAmenities = typeof response.data.amenities === 'string' 
                ? JSON.parse(response.data.amenities) 
                : response.data.amenities;
              
              if (Array.isArray(parsedAmenities)) {
                if (parsedAmenities.length > 0 && typeof parsedAmenities[0] === 'object' && parsedAmenities[0].id) {
                  propertyAmenities = parsedAmenities.map(amenity => String(amenity.id));
                } else {
                  propertyAmenities = parsedAmenities.map(id => String(id));
                }
              }
            }
          } catch (error) {
            console.error('Error parsing property amenities:', error);
            propertyAmenities = [];
          }
          
          setPropertyAmenityIds(propertyAmenities);
          
          const transformedProperty = transformProperty(response.data);
          setProperty(transformedProperty);

          // Fetch amenities metadata using property_type from API so we get the correct list
          const propertyType = response.data.property_type != null ? String(response.data.property_type) : null;
          if (propertyType) {
            try {
              const metaResponse = await getApi('/property/metadata', {
                params: { property_type: propertyType }
              });
              if (metaResponse?.statusCode === 200 && metaResponse?.data?.amenities) {
                const amenities = metaResponse.data.amenities
                  .filter(amenity => amenity.is_active === '1')
                  .map(amenity => ({
                    id: String(amenity.id),
                    name: amenity.name
                  }));
                setAmenitiesList(amenities);
              }
            } catch (metaError) {
              console.error('Error fetching amenities metadata:', metaError);
              setAmenitiesList([]);
            }
          } else {
            setAmenitiesList([]);
          }
          
          // Fetch share link from API
          setShareLinkLoading(true);
          try {
            const shareResponse = await postApi(`/property/${id}/share`, {});
            const url = shareResponse?.data?.url;
            if (url) {
              setShareLink(url);
            }
          } catch (shareError) {
            console.error('Error generating share link:', shareError);
            toast.error(shareError?.response?.data?.message || 'Failed to generate share link');
          } finally {
            setShareLinkLoading(false);
          }
          
          // Set all property images
          const images = response.data.property_images && response.data.property_images.length > 0
            ? response.data.property_images.map(img => img.image_url)
            : ['https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800'];
          setPropertyImages(images);
          setCurrentImageIndex(0);
        } else {
          toast.error(response?.message || 'Failed to fetch property details');
          navigate('/landlord/properties');
        }
      } catch (error) {
        console.error('Error fetching property:', error);
        toast.error('Failed to load property details. Please try again.');
        navigate('/landlord/properties');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const propertyStatus = property?.status;
  const canMarkAsCompleted =
    propertyStatus === PROPERTY_LISTING_STATUS.AVAILABLE ||
    propertyStatus === PROPERTY_LISTING_STATUS.PENDING ||
    propertyStatus === PROPERTY_LISTING_STATUS.RENTED;
  const isPropertyCompleted = propertyStatus === PROPERTY_LISTING_STATUS.COMPLETED;

  const handleCompleteProperty = async () => {
    if (!id || completeLoading || !canMarkAsCompleted) return;

    setCompleteLoading(true);
    try {
      const response = await patchApi(`/property/update-status/${id}`, {
        status: PROPERTY_LISTING_STATUS.COMPLETED,
      });
      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Property listing status updated successfully.');
        setProperty((prev) =>
          prev ? { ...prev, status: PROPERTY_LISTING_STATUS.COMPLETED } : prev
        );
      } else {
        toast.error(response?.message || 'Failed to update property status');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update property status');
    } finally {
      setCompleteLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!shareLink) {
      toast.error('Share link not available yet');
      return;
    }
    if (navigator?.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(shareLink);
      toast.success('Link copied to clipboard!');
      return;
    }
    // Fallback for HTTP or when clipboard API is not available
    const textarea = document.createElement('textarea');
    textarea.value = shareLink;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success('Link copied to clipboard!');
      } else {
        toast.error('Unable to copy link. Please copy it manually.');
      }
    } finally {
      document.body.removeChild(textarea);
    }
  };

  // Auto-rotate carousel (pause when modal is open)
  useEffect(() => {
    if (propertyImages.length > 1 && !isImageModalOpen) {
      carouselIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % propertyImages.length
        );
      }, 5000); // Change image every 5 seconds
    } else {
      // Clear interval when modal is open or only one image
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
    }

    return () => {
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
    };
  }, [propertyImages.length, isImageModalOpen]);

  // Navigate carousel (pause auto-rotation when user interacts)
  const goToNextImage = () => {
    // Pause auto-rotation temporarily
    if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
    }
    setCurrentImageIndex((prevIndex) => 
      (prevIndex + 1) % propertyImages.length
    );
    // Resume after 10 seconds
    setTimeout(() => {
      if (propertyImages.length > 1 && !isImageModalOpen) {
        carouselIntervalRef.current = setInterval(() => {
          setCurrentImageIndex((prevIndex) => 
            (prevIndex + 1) % propertyImages.length
          );
        }, 5000);
      }
    }, 10000);
  };

  const goToPrevImage = () => {
    // Pause auto-rotation temporarily
    if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
    }
    setCurrentImageIndex((prevIndex) => 
      (prevIndex - 1 + propertyImages.length) % propertyImages.length
    );
    // Resume after 10 seconds
    setTimeout(() => {
      if (propertyImages.length > 1 && !isImageModalOpen) {
        carouselIntervalRef.current = setInterval(() => {
          setCurrentImageIndex((prevIndex) => 
            (prevIndex + 1) % propertyImages.length
          );
        }, 5000);
      }
    }, 10000);
  };

  // Open image modal
  const openImageModal = (index) => {
    setModalImageIndex(index);
    setIsImageModalOpen(true);
  };

  // Close image modal
  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  // Navigate modal carousel
  const goToNextModalImage = () => {
    setModalImageIndex((prevIndex) => 
      (prevIndex + 1) % propertyImages.length
    );
  };

  const goToPrevModalImage = () => {
    setModalImageIndex((prevIndex) => 
      (prevIndex - 1 + propertyImages.length) % propertyImages.length
    );
  };

  const getInitials = (firstName, lastName) => {
    const first = (firstName || '')[0] || '';
    const last = (lastName || '')[0] || '';
    const initials = `${first}${last}`.toUpperCase();
    return initials || '?';
  };

  const getStatusColor = (status) => {
    if (status === 'Approved' || status === 'Verified') return '#155724';
    if (status === 'Declined') return '#721c24';
    if (status === 'Pending') return '#856404';
    return '#666';
  };

  const getStatusBgColor = (status) => {
    if (status === 'Approved' || status === 'Verified') return '#d4edda';
    if (status === 'Declined') return '#f8d7da';
    if (status === 'Pending') return '#fff3cd';
    return '#e0e0e0';
  };

  // Grade badge colors: 1 = light green, 2 = light blue, 3 = light red, other = neutral
  const getGradeStyle = (grade) => {
    const g = grade != null && grade !== '' ? String(grade).trim() : '';
    if (g === '1') return { backgroundColor: '#d4edda', color: '#155724' };
    if (g === '2') return { backgroundColor: '#ABF0FD', color: '#004085' };
    if (g === '3') return { backgroundColor: '#f8d7da', color: '#721c24' };
    return { backgroundColor: '#e9ecef', color: '#495057' };
  };

  if (isLoading || !property) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading property details...
      </div>
    );
  }

  return (
    <div className="property-detail-page">
      <div className="screenings-actions-bar" style={{ marginBottom: '20px' }}>
        <button
          type="button"
          className="screenings-back-button"
          onClick={() => navigate('/landlord/properties')}
          aria-label="Go back"
        >
          <span className="screenings-back-button-icon" aria-hidden="true">
            ←
          </span>
        </button>
      </div>
      <div className="property-detail-container">
        <div className="property-detail-left">
          <div className="property-detail-header">
            <div className="property-detail-address-section">
              <h1 className="property-detail-address">{property.address}</h1>
              <div className="property-detail-code-location">
                <span className="property-detail-code">Code: {property.code}</span>
                <span className="property-detail-location">{property.city} {property.zipCode}</span>
              </div>
              {(property.llcLogo || property.llcName) && (
                <div className="property-detail-llc">
                  {property.llcLogo && !llcLogoError ? (
                    <img
                      src={property.llcLogo}
                      alt={property.llcName ? `${property.llcName} logo` : 'Business logo'}
                      className="property-detail-llc-logo"
                      onError={() => setLlcLogoError(true)}
                    />
                  ) : (
                    <span className="property-detail-llc-fallback" aria-hidden="true">
                      {(property.llcName || '?').trim().charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="property-detail-llc-text">
                    <span className="property-detail-llc-label">Managed by</span>
                    <span className="property-detail-llc-name">{property.llcName || 'Business/LLC'}</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="property-detail-image-container">
            {propertyImages.length > 0 ? (
              <div className="property-detail-carousel">
                <div className="property-detail-carousel-image-container"style={{ position: 'relative', width: '100%', height:  '400px'}}>
                <img
                  src={propertyImages[currentImageIndex]}
                  alt={`${property.address} - Image ${currentImageIndex + 1}`}
                  className="property-detail-image"
                  onClick={() => openImageModal(currentImageIndex)}
                  style={{ cursor: 'pointer', width: '100%', height: 'auto', display: 'block' }}
                  onError={() => setImageError(true)}
                />
                
                {/* Carousel Navigation Arrows */}
                {propertyImages.length > 1 && (
                  <>
                    <button
                      className="property-detail-carousel-prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        goToPrevImage();
                      }}
                      style={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        cursor: 'pointer',
                        fontSize: '20px',
                        zIndex: 2
                      }}
                    >
                      ‹
                    </button>
                    <button
                      className="property-detail-carousel-next"
                      onClick={(e) => {
                        e.stopPropagation();
                        goToNextImage();
                      }}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        cursor: 'pointer',
                        fontSize: '20px',
                        zIndex: 2
                      }}
                    >
                      ›
                    </button>
                    
                    {/* Carousel Indicators */}
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '8px',
                        zIndex: 2
                      }}
                    >
                      {propertyImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(index);
                          }}
                          style={{
                            width: currentImageIndex === index ? '10px' : '10px',
                            height: '10px',
                            borderRadius: '50%',
                            border: 'none',
                            background: currentImageIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              </div>
            ) : (
              <div className="property-detail-image-placeholder"></div>
            )}
          </div>

          <div className="property-detail-about">
            <h2 className="property-detail-about-title">About this Property</h2>
            <p className="property-detail-about-description">{property.description}</p>
          </div>
          <div className="property-detail-financials">
            <div className="property-detail-financial-card property-detail-financial-card-rent">
              <div className="property-detail-financial-label">Monthly Rent</div>
              <div className="property-detail-financial-value">{property.price}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Sq. ft</div>
              <div className="property-detail-financial-value">{property.sqft}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Type</div>
              <div className="property-detail-financial-value">{property.type}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Deposit</div>
              <div className="property-detail-financial-value">{property.deposit}</div>
            </div>
          </div>
          {showApplicants && (
            <div className="property-detail-applicants">
              <div className="property-detail-applicants-header">
                <h2 className="property-detail-applicants-title">Recent Applicants</h2>
                <span className="property-detail-applicants-count">{property.totalApplicants ?? property.applicants.length} Total</span>
              </div>
              <div className="property-detail-applicants-list">
                {property.applicants.map((applicant) => (
                  <div 
                    key={applicant.id} 
                    className="property-detail-applicant-card"
                    onClick={() => navigate(`/landlord/properties/${id}/tenant/${applicant.id}`)}
                  >
                    <div className="property-detail-applicant-avatar">
                      {getInitials(applicant.firstName, applicant.lastName)}
                    </div>
                    <div className="property-detail-applicant-info">
                      <div className="property-detail-applicant-name">
                        {applicant.firstName} {applicant.lastName}
                      </div>
                    </div>
                    <div className="property-detail-applicant-badges">
                      <span className="property-detail-applicant-grade" style={getGradeStyle(applicant.grade)}>
                        Grade: {applicant.grade}
                      </span>
                      <span 
                        className="property-detail-applicant-status"
                        style={{ 
                          color: getStatusColor(applicant.status),
                          backgroundColor: getStatusBgColor(applicant.status)
                        }}
                      >
                        {applicant.status}
                      </span>
                    </div>
                    <div className="property-detail-applicant-arrow">
                      <img 
                        src={window.getPublicUrl ? window.getPublicUrl("/assets/images/ArrorRighticon.svg") : "/assets/images/ArrorRighticon.svg"} 
                        alt="View" 
                        className="property-detail-applicant-arrow-icon"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="property-detail-right">
          <div className="property-detail-share">
            <label className="property-detail-share-label">Share link:</label>
            <div className="property-detail-share-input-wrapper">
              <input
                type="text"
                className="property-detail-share-input"
                value={shareLinkLoading ? 'Generating link...' : shareLink}
                readOnly
              />
              <button 
                className="property-detail-share-copy"
                onClick={handleCopyLink}
                disabled={shareLinkLoading || !shareLink}
                type="button"
              >
                <img 
                  src="/assets/images/CopyIcon.svg" 
                  alt="Copy" 
                  className="property-detail-share-copy-icon"
                />
              </button>
            </div>
          </div>
          <div className="property-detail-features">
            <h3 className="property-detail-features-title">Property Features

            </h3>
            <div className="property-detail-features-grid for-deailsview-cs">
              {/* Display all amenities from metadata */}
              {amenitiesList.length > 0 ? (
                amenitiesList.map((amenity) => {
                  // Check if this amenity ID is in the property's amenities (both are strings)
                  const hasAmenity = propertyAmenityIds.includes(amenity.id);
                  return (
                    <div key={amenity.id} className="property-detail-feature-item">
                      <span className="property-detail-feature-label">{amenity.name}:</span>
                      <span className="property-detail-feature-value">{hasAmenity ? 'Yes' : 'No'}</span>
                    </div>
                  );
                })
              ) : (
                <div className="property-detail-feature-item">
                  <span className="property-detail-feature-label">Loading amenities...</span>
                </div>
              )}
              {/* Bedroom and Bathroom (always show) */}
              <div className="property-detail-feature-item">
                <span className="property-detail-feature-label">Bedroom:</span>
                <span className="property-detail-feature-value">{property.bedrooms}</span>
              </div>
              <div className="property-detail-feature-item">
                <span className="property-detail-feature-label">Bathroom:</span>
                <span className="property-detail-feature-value">{property.bathrooms}</span>
              </div>
            </div>
          </div>

          {(canMarkAsCompleted || isPropertyCompleted) && (
            <button
              type="button"
              className="btn btn-success property-detail-complete-btn"
              onClick={handleCompleteProperty}
              disabled={completeLoading || isPropertyCompleted}
            >
              {completeLoading ? 'Completing...' : isPropertyCompleted ? 'Completed' : 'Complete'}
            </button>
          )}
        </div>
      </div>

      {/* Image Modal with Carousel */}
      <PropertyImageModal
        isOpen={isImageModalOpen}
        onClose={closeImageModal}
        images={propertyImages}
        currentIndex={modalImageIndex}
        onNext={goToNextModalImage}
        onPrev={goToPrevModalImage}
        onIndicatorClick={setModalImageIndex}
      />
    </div>
  );
};

export default PropertyDetail;
